// Der Stand der eigenen Terminabstimmungen — die Seite, die dem Organisator
// bisher gefehlt hat.
//
// Der gemeldete Mangel (05.08.2026): „Hast du mir nur darauf per Email
// geantwortet, oder ueber den Link was ausgewaehlt? Ich habe neben deiner Email
// nichts bekommen." Eine Teilnehmerin hatte per gewoehnlicher Mail geantwortet
// statt ueber den Link — und der Organisator hatte KEINEN Weg, nachzusehen, ob
// jemand abgestimmt hat. Es gab weder eine Liste (Prod: `GET /calendar/polls`
// → 405) noch eine Seite; die Kennung der frisch angelegten Abstimmung lebte
// nur im Speicher des Wizard-Zustands (`CalendarMs.tsx`, `pollCreated`).
//
// Entschieden ist: **kein zusaetzlicher Mailverkehr** — keine Mail je Stimme,
// kein Digest, keine Erinnerung an den Organisator. Die bewusste Folge: er muss
// aktiv nachsehen. Damit ist die AUFFINDBARKEIT die eigentliche Aufgabe dieser
// Seite, nicht ihre Darstellung. Eine Statusseite, die man nur kennt, solange
// der Tab offen ist, loeste gar nichts.
//
// Drei Wege fuehren deshalb hierher, und keiner davon haengt an einem offenen Tab:
//
//   1. **Diese Route** `/abstimmungen` — anmeldepflichtig, im Atlas-Rahmen,
//      lesezeichenfaehig. `/abstimmungen/<id>` ist der Deep-Link auf eine
//      einzelne Abstimmung.
//   2. **Der Einstieg im Kalender** (`CalendarMs.tsx`, Werkzeugleiste) mit der
//      Zahl der laufenden Abstimmungen. Dort wird die Abstimmung angelegt, dort
//      sucht der Organisator sie am naechsten Tag wieder.
//   3. **Der Bestaetigungs-Block direkt nach dem Anlegen** verlinkt auf den
//      Stand. Das ist der bequemste Weg — und allein waere er genau der Fall,
//      den die Aufgabe ausschliesst (Browser zu, weg).
//
// Der Zustand steckt vollstaendig in der URL und im Bearer-Token (localStorage).
// Diese Seite haelt NICHTS im Speicher, was einen Neustart nicht ueberlebt:
// genau das ist die Zusage „auch am naechsten Tag wiederfindbar".
//
// Route `/abstimmungen` steht bewusst NICHT in `vite.config.ts::API_PATHS` — sie
// ist eine SPA-Route und muss beim Frontend bleiben. Der Datenpfad
// `/calendar/polls` liegt dagegen unter dem bereits gefuehrten Praefix
// `/calendar`; gegen die Praefix-Whitelist der Prod-Kante gemessen (05.08.2026):
// `GET /calendar/polls` → 405 application/json (erreicht das Backend),
// `GET /abstimmungen` → 200 text/html (SPA-Fallback). Beides so gewollt.
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  CalendarCheck2,
  CalendarClock,
  Check,
  Clock,
  ListChecks,
  Mail,
  RefreshCw,
  ServerCrash,
  Users,
} from "lucide-react";
import { api, ApiError } from "../api";
import { useT, type Katalog } from "../i18n";
import {
  Button,
  Chip,
  EmptyState,
  PageHeader,
  Skeleton,
} from "../ui";

// ── Vertrag mit dem Server ───────────────────────────────────────────────────
// Form aus `app/schemas/calendar_poll.py`. Nur die Felder, die diese Seite auch
// anzeigt — was sie nicht zeigt, holt sie nicht.

interface Slot {
  id: string;
  starts_at: string;
  ends_at: string;
}

/** `PollSummaryOut` — ein Eintrag der Liste. */
interface PollSummary {
  id: string;
  subject: string;
  status: string;
  expires_at: string;
  created_at: string;
  duration_minutes: number;
  slot_count: number;
  participant_count: number;
  responded_count: number;
  /** Gerechnet (`accepts_votes`), NICHT `status === "open"`: abgelaufene
   *  Abstimmungen stehen weiterhin als `open` in der Tabelle, weil es den
   *  Aufraeum-Job nicht gibt. Wer `status` liest, behauptet „laeuft noch". */
  open_for_votes: boolean;
  /** Was als Naechstes passiert, als deutscher Satz — vom Server, damit es
   *  nicht zwei Formulierungen derselben Lage gibt. */
  next_step: string;
  booked_slot: Slot | null;
}

interface Participant {
  id: string;
  email: string;
  name: string | null;
  role: string;
  responded_at: string | null;
  invited_at: string | null;
  reminded_at: string | null;
  selected_slot_ids: string[];
}

/** `PollOut` — die Detailansicht. */
interface PollDetail extends Omit<PollSummary, "slot_count" | "participant_count" | "responded_count"> {
  description: string | null;
  organizer_upn: string;
  online_meeting: boolean;
  slots: Slot[];
  participants: Participant[];
  booked_at: string | null;
  graph_web_link: string | null;
  booking_error: string | null;
}

// ── Deutsche Zeitangaben ─────────────────────────────────────────────────────
// Feste Zeitzone + festes Locale, exakt wie `pages/Termin.tsx` und
// `app/mail/calendar_polls.py`: Mail, Kundenseite und diese Seite muessen
// dieselbe Stunde nennen.
const TZ = "Europe/Berlin";
const DATE_FMT = new Intl.DateTimeFormat("de-DE", {
  timeZone: TZ,
  weekday: "long",
  day: "numeric",
  month: "long",
});
const TIME_FMT = new Intl.DateTimeFormat("de-DE", {
  timeZone: TZ,
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
});
const STAMP_FMT = new Intl.DateTimeFormat("de-DE", {
  timeZone: TZ,
  day: "2-digit",
  month: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
});

const datum = (iso: string) => DATE_FMT.format(new Date(iso));
const uhr = (iso: string) => TIME_FMT.format(new Date(iso));
/** „05.08., 14:12" — fuer Zeitpunkte, bei denen die Minute zaehlt (Antworten). */
const stempel = (iso: string) => STAMP_FMT.format(new Date(iso)).replace(",", ",");

function zeitfenster(slot: Slot): string {
  return `${datum(slot.starts_at)}, ${uhr(slot.starts_at)}–${uhr(slot.ends_at)} Uhr`;
}

/** Der Zustand in EINEM Wort, fuer das Chip. Haengt an `open_for_votes` und
 *  erst danach an `status` — eine abgelaufene Abstimmung ist „beendet", auch
 *  wenn in der Tabelle `open` steht. */
function zustand(
  t: Katalog,
  p: { status: string; open_for_votes: boolean }
): {
  label: string;
  tone: "neutral" | "petrol" | "success" | "danger";
} {
  const k = t.calendar.polls;
  if (p.status === "booked") return { label: k.zustandGebucht, tone: "success" };
  if (p.status === "booking") return { label: k.zustandWirdGebucht, tone: "petrol" };
  if (p.status === "booking_failed")
    return { label: k.zustandBuchungFehlgeschlagen, tone: "danger" };
  if (p.status === "no_common_slot")
    return { label: k.zustandKeinGemeinsamerTermin, tone: "danger" };
  if (p.status === "cancelled") return { label: k.zustandAbgesagt, tone: "neutral" };
  if (!p.open_for_votes) return { label: k.zustandFristAbgelaufen, tone: "neutral" };
  return { label: k.zustandLaeuft, tone: "petrol" };
}

function anzeigename(p: Participant): string {
  return p.name?.trim() || p.email;
}

// ── Bausteine ────────────────────────────────────────────────────────────────

/** Der Satz „was passiert als Naechstes". Er ist der Grund, warum es diese
 *  Seite gibt, und steht deshalb ganz oben und nicht im Kleingedruckten. */
function NaechsterSchritt({ text, warnend }: { text: string; warnend: boolean }) {
  return (
    <div
      data-poll-next-step="1"
      className={[
        "flex items-start gap-2.5 rounded-xl px-4 py-3 text-sm leading-relaxed",
        warnend ? "bg-warning-soft text-text" : "bg-surface-2 text-text-2",
      ].join(" ")}
    >
      <Clock className="mt-0.5 h-4 w-4 shrink-0 text-text-3" aria-hidden />
      <span>{text}</span>
    </div>
  );
}

/** Ein Vorschlag mit seiner Zustimmung. Der Balken ist bewusst kein
 *  `ValueBar`-Primitiv: hier ist das Maximum die Zahl der Eingeladenen und
 *  nicht das Listen-Maximum — „2 von 3" muss auch dann 2/3 fuellen, wenn kein
 *  anderer Vorschlag mehr Zustimmung hat. */
function VorschlagZeile({
  slot,
  zusagen,
  eingeladen,
  gebucht,
}: {
  slot: Slot;
  zusagen: Participant[];
  eingeladen: number;
  gebucht: boolean;
}) {
  const t = useT();
  const pct = eingeladen > 0 ? Math.round((zusagen.length / eingeladen) * 100) : 0;
  return (
    <div
      data-poll-slot={slot.id}
      data-poll-slot-votes={zusagen.length}
      className={[
        "rounded-xl border px-4 py-3",
        gebucht ? "border-accent-500 bg-accent-50" : "border-border bg-surface",
      ].join(" ")}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            {gebucht && (
              <CalendarCheck2
                className="h-4 w-4 shrink-0 text-accent-600"
                aria-hidden
              />
            )}
            <span className="text-sm font-medium capitalize text-text">
              {zeitfenster(slot)}
            </span>
          </div>
          <div className="mt-1 text-sm text-text-2">
            {zusagen.length === 0
              ? t.calendar.polls.nochKeineZusage
              : zusagen.map(anzeigename).join(", ")}
          </div>
        </div>
        <div className="shrink-0 text-right">
          <div className="font-numeric text-sm font-semibold tabular-nums text-text">
            {zusagen.length} / {eingeladen}
          </div>
          <div
            className="mt-1.5 h-[5px] w-16 overflow-hidden rounded-full bg-bar-track"
            aria-hidden
          >
            <div
              className="h-full rounded-full bg-bar-fill/55"
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

/** Ein Eingeladener mit seinem Stand. Die drei Faelle, die der Organisator
 *  auseinanderhalten muss: hat zugesagt (mit Zeitpunkt), hat geantwortet aber
 *  kann keinen, hat gar nicht geantwortet. */
function TeilnehmerZeile({ p }: { p: Participant }) {
  const t = useT();
  const geantwortet = p.responded_at !== null;
  const kannKeinen = geantwortet && p.selected_slot_ids.length === 0;
  return (
    <div
      data-poll-participant={p.email}
      data-poll-participant-responded={geantwortet ? "1" : "0"}
      className="flex items-start justify-between gap-3 border-b border-border py-2.5 last:border-b-0"
    >
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="text-sm font-medium text-text">{anzeigename(p)}</span>
          {p.role === "optional" && (
            <Chip variant="hebel">{t.calendar.polls.optionalChip}</Chip>
          )}
        </div>
        <div className="mt-0.5 truncate text-xs text-text-3">{p.email}</div>
        {/* Der Einladungs-Stand gehoert hierher, weil er die erste Frage
            beantwortet, wenn jemand nicht antwortet: hat er ueberhaupt eine
            Mail bekommen? `invited_at = null` heisst, der Versand ist
            fehlgeschlagen — dann laeuft fuer ihn auch keine Erinnerungsfrist. */}
        {p.invited_at === null ? (
          <div
            data-poll-not-invited="1"
            className="mt-1 flex items-center gap-1.5 text-xs text-danger"
          >
            <Mail className="h-3 w-3 shrink-0" aria-hidden />
            {t.calendar.polls.einladungNichtZugestellt}
          </div>
        ) : (
          p.reminded_at !== null && (
            <div className="mt-1 text-xs text-text-3">
              {t.calendar.polls.erinnertAm(stempel(p.reminded_at))}
            </div>
          )
        )}
      </div>
      <div className="shrink-0 text-right">
        {geantwortet ? (
          <>
            <Chip variant="status" tone={kannKeinen ? "neutral" : "success"}>
              {kannKeinen
                ? t.calendar.polls.kannKeinen
                : p.selected_slot_ids.length === 1
                  ? t.calendar.polls.eineZeit
                  : t.calendar.polls.nZeiten(p.selected_slot_ids.length)}
            </Chip>
            <div className="mt-1 whitespace-nowrap text-xs text-text-3">
              {stempel(p.responded_at as string)}
              {t.calendar.polls.uhrSuffix}
            </div>
          </>
        ) : (
          <Chip variant="status" tone="neutral">
            {t.calendar.polls.keineAntwort}
          </Chip>
        )}
      </div>
    </div>
  );
}

function Ladebalken() {
  return (
    <div className="space-y-3">
      <Skeleton className="h-20 w-full rounded-xl" />
      <Skeleton className="h-20 w-full rounded-xl" />
    </div>
  );
}

function Stoerung({ onRetry }: { onRetry: () => void }) {
  const t = useT();
  return (
    <EmptyState
      icon={<ServerCrash className="h-6 w-6" />}
      title={t.calendar.polls.stoerungTitel}
      hint={t.calendar.polls.stoerungHinweis}
      action={
        <Button
          variant="primary"
          icon={<RefreshCw className="h-4 w-4" />}
          onClick={onRetry}
        >
          {t.common.erneutVersuchen}
        </Button>
      }
    />
  );
}

// ── Liste ────────────────────────────────────────────────────────────────────

function Liste() {
  const t = useT();
  const [polls, setPolls] = useState<PollSummary[] | null>(null);
  const [fehler, setFehler] = useState(false);
  const [laedt, setLaedt] = useState(true);

  const laden = useCallback(async () => {
    setLaedt(true);
    setFehler(false);
    try {
      setPolls(await api.get<PollSummary[]>("/calendar/polls"));
    } catch {
      setFehler(true);
    } finally {
      setLaedt(false);
    }
  }, []);

  useEffect(() => {
    void laden();
  }, [laden]);

  const laufend = polls?.filter((p) => p.open_for_votes) ?? [];
  const beendet = polls?.filter((p) => !p.open_for_votes) ?? [];

  return (
    <div className="h-full overflow-y-auto px-6 py-6">
      <div className="mx-auto w-full max-w-3xl space-y-5">
        <PageHeader
          title={t.calendar.polls.seitenTitel}
          description={t.calendar.polls.seitenBeschreibung}
          actions={
            <Button
              variant="secondary"
              size="sm"
              className="px-2"
              aria-label={t.common.aktualisieren}
              title={t.common.aktualisieren}
              icon={<RefreshCw className={laedt ? "h-4 w-4 animate-spin" : "h-4 w-4"} />}
              onClick={() => void laden()}
              disabled={laedt}
            />
          }
        />

        {laedt && polls === null ? (
          <Ladebalken />
        ) : fehler ? (
          <Stoerung onRetry={() => void laden()} />
        ) : polls && polls.length === 0 ? (
          <EmptyState
            icon={<ListChecks className="h-6 w-6" />}
            title={t.calendar.polls.leerTitel}
            hint={t.calendar.polls.leerHinweis}
            action={
              <Button variant="primary" onClick={() => (window.location.href = "/calendar")}>
                {t.calendar.polls.zumKalender}
              </Button>
            }
          />
        ) : (
          <>
            <Abschnitt
              titel={t.calendar.polls.laeuftNoch}
              eintraege={laufend}
              leer={t.calendar.polls.laeuftNochLeer}
            />
            <Abschnitt titel={t.calendar.polls.abgeschlossen} eintraege={beendet} leer={null} />
          </>
        )}
      </div>
    </div>
  );
}

function Abschnitt({
  titel,
  eintraege,
  leer,
}: {
  titel: string;
  eintraege: PollSummary[];
  leer: string | null;
}) {
  if (eintraege.length === 0 && leer === null) return null;
  return (
    <section data-poll-section={titel} className="space-y-2">
      <h2 className="text-sm font-semibold text-text-2">{titel}</h2>
      {eintraege.length === 0 ? (
        <p className="rounded-xl border border-dashed border-border px-4 py-3 text-sm text-text-3">
          {leer}
        </p>
      ) : (
        eintraege.map((p) => <ListenKarte key={p.id} poll={p} />)
      )}
    </section>
  );
}

function ListenKarte({ poll }: { poll: PollSummary }) {
  const t = useT();
  const z = zustand(t, poll);
  const offen = poll.participant_count - poll.responded_count;
  return (
    <Link
      to={`/abstimmungen/${poll.id}`}
      data-poll-card={poll.id}
      className="block rounded-xl border border-border bg-surface p-4 transition-colors hover:bg-surface-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-500/60"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="truncate text-sm font-medium text-text">{poll.subject}</div>
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-text-2">
            <span className="inline-flex items-center gap-1">
              <Users className="h-3.5 w-3.5 shrink-0 text-text-3" aria-hidden />
              <span data-poll-card-count>
                {t.calendar.polls.habenAbgestimmt(
                  poll.responded_count,
                  poll.participant_count
                )}
              </span>
            </span>
            <span className="inline-flex items-center gap-1">
              <CalendarClock className="h-3.5 w-3.5 shrink-0 text-text-3" aria-hidden />
              {t.calendar.polls.vorschlaege(poll.slot_count)}
            </span>
          </div>
        </div>
        <Chip variant="status" tone={z.tone} className="shrink-0">
          {z.label}
        </Chip>
      </div>
      <div className="mt-2.5 text-xs leading-relaxed text-text-2">
        {poll.open_for_votes ? (
          <>
            {offen === 0
              ? t.calendar.polls.alleHabenAbgestimmt
              : offen === 1
                ? t.calendar.polls.eineRueckmeldungFehlt
                : t.calendar.polls.rueckmeldungenFehlen(offen)}{" "}
            {t.calendar.polls.frist}{" "}
            <span className="capitalize">{datum(poll.expires_at)}</span>
            {t.calendar.polls.fristZeit(uhr(poll.expires_at))}
          </>
        ) : (
          poll.next_step
        )}
      </div>
    </Link>
  );
}

// ── Detail ───────────────────────────────────────────────────────────────────

function Detail({ pollId }: { pollId: string }) {
  const t = useT();
  const navigate = useNavigate();
  const [poll, setPoll] = useState<PollDetail | null>(null);
  const [fehler, setFehler] = useState<"weg" | "stoerung" | null>(null);
  const [laedt, setLaedt] = useState(true);

  const laden = useCallback(async () => {
    setLaedt(true);
    setFehler(null);
    try {
      setPoll(await api.get<PollDetail>(`/calendar/polls/${pollId}`));
    } catch (e) {
      // 404 ist hier KEINE Stoerung, sondern eine Auskunft: die Abstimmung
      // gehoert jemand anderem oder gibt es nicht mehr (der Server macht
      // „fremd" und „unbekannt" bewusst ununterscheidbar).
      setFehler(e instanceof ApiError && e.status === 404 ? "weg" : "stoerung");
    } finally {
      setLaedt(false);
    }
  }, [pollId]);

  useEffect(() => {
    void laden();
  }, [laden]);

  const zusagenJeSlot = useMemo(() => {
    const out = new Map<string, Participant[]>();
    for (const s of poll?.slots ?? []) {
      out.set(
        s.id,
        (poll?.participants ?? []).filter((p) => p.selected_slot_ids.includes(s.id))
      );
    }
    return out;
  }, [poll]);

  const zurueck = (
    <button
      type="button"
      data-poll-back="1"
      onClick={() => navigate("/abstimmungen")}
      className="inline-flex items-center gap-1.5 text-sm text-text-2 hover:text-text focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-500/60 rounded"
    >
      <ArrowLeft className="h-4 w-4" aria-hidden />
      {t.calendar.polls.alleAbstimmungen}
    </button>
  );

  if (laedt && poll === null) {
    return (
      <div className="h-full overflow-y-auto px-6 py-6">
        <div className="mx-auto w-full max-w-3xl space-y-5">
          {zurueck}
          <Ladebalken />
        </div>
      </div>
    );
  }

  if (fehler === "weg" || (!poll && !laedt && fehler !== "stoerung")) {
    return (
      <div className="h-full overflow-y-auto px-6 py-6">
        <div className="mx-auto w-full max-w-3xl space-y-5">
          {zurueck}
          <EmptyState
            icon={<ListChecks className="h-6 w-6" />}
            title={t.calendar.polls.gibtEsNichtTitel}
            hint={t.calendar.polls.gibtEsNichtHinweis}
          />
        </div>
      </div>
    );
  }

  if (fehler === "stoerung" || !poll) {
    return (
      <div className="h-full overflow-y-auto px-6 py-6">
        <div className="mx-auto w-full max-w-3xl space-y-5">
          {zurueck}
          <Stoerung onRetry={() => void laden()} />
        </div>
      </div>
    );
  }

  const z = zustand(t, poll);
  const geantwortet = poll.participants.filter((p) => p.responded_at !== null);
  const fehlend = poll.participants.filter((p) => p.responded_at === null);
  const eingeladen = poll.participants.length;

  return (
    <div className="h-full overflow-y-auto px-6 py-6">
      <div
        className="mx-auto w-full max-w-3xl space-y-5"
        data-poll-detail={poll.id}
        data-poll-open={poll.open_for_votes ? "1" : "0"}
      >
        {zurueck}

        <PageHeader
          title={poll.subject}
          description={
            <span className="inline-flex flex-wrap items-center gap-x-3 gap-y-1">
              <Chip variant="status" tone={z.tone}>
                {z.label}
              </Chip>
              <span>
                {t.calendar.polls.dauerUndFrist(poll.duration_minutes)}{" "}
                <span className="capitalize">{datum(poll.expires_at)}</span>
                {t.calendar.polls.fristZeit(uhr(poll.expires_at))}
              </span>
            </span>
          }
          actions={
            <Button
              variant="secondary"
              size="sm"
              className="px-2"
              aria-label={t.common.aktualisieren}
              title={t.common.aktualisieren}
              icon={<RefreshCw className={laedt ? "h-4 w-4 animate-spin" : "h-4 w-4"} />}
              onClick={() => void laden()}
              disabled={laedt}
            />
          }
        />

        {/* Was als Naechstes passiert — der Satz kommt vom Server. Warnend
            eingefaerbt, wenn von allein nichts mehr passiert: genau dort muss
            der Organisator handeln. */}
        <NaechsterSchritt
          text={poll.next_step}
          warnend={
            !poll.open_for_votes &&
            poll.status !== "booked" &&
            poll.status !== "booking"
          }
        />

        {poll.booking_error && (
          <p
            data-poll-booking-error="1"
            className="rounded-xl bg-danger-soft px-4 py-3 text-sm leading-relaxed text-text"
          >
            {t.calendar.polls.buchungsfehler(poll.booking_error)}
          </p>
        )}

        {/* Zusammenfassung in drei Zahlen. Auf 390 px untereinander, ab sm
            nebeneinander — Inhalt bestimmt die Groesse, nicht umgekehrt. */}
        <div className="grid grid-cols-3 gap-2 sm:gap-3">
          <Kennzahl label={t.calendar.polls.kennzahlEingeladen} wert={eingeladen} />
          <Kennzahl label={t.calendar.polls.kennzahlAbgestimmt} wert={geantwortet.length} />
          <Kennzahl
            label={t.calendar.polls.kennzahlOffen}
            wert={fehlend.length}
            betont={fehlend.length > 0}
          />
        </div>

        <section className="space-y-2">
          <h2 className="text-sm font-semibold text-text-2">
            {t.calendar.polls.zustimmungJeVorschlag}
          </h2>
          <div className="space-y-2">
            {poll.slots.map((s) => (
              <VorschlagZeile
                key={s.id}
                slot={s}
                zusagen={zusagenJeSlot.get(s.id) ?? []}
                eingeladen={eingeladen}
                gebucht={poll.booked_slot?.id === s.id}
              />
            ))}
          </div>
          {/* Der Zustand „niemand hat geantwortet" muss genauso lesbar sein wie
              „alle haben". Ohne diesen Satz stuenden dort nur Nullen, und eine
              Wand aus Nullen liest sich wie ein Defekt. */}
          {geantwortet.length === 0 && (
            <p
              data-poll-nobody="1"
              className="rounded-xl border border-dashed border-border px-4 py-3 text-sm leading-relaxed text-text-2"
            >
              {t.calendar.polls.niemandAbgestimmt}{" "}
              {poll.open_for_votes
                ? t.calendar.polls.niemandAbgestimmtOffen
                : t.calendar.polls.niemandAbgestimmtBeendet}
            </p>
          )}
        </section>

        <section className="space-y-2">
          <h2 className="text-sm font-semibold text-text-2">
            {t.calendar.polls.eingeladeneAnzahl(eingeladen)}
          </h2>
          <div className="rounded-xl border border-border bg-surface px-4">
            {poll.participants.map((p) => (
              <TeilnehmerZeile key={p.id} p={p} />
            ))}
          </div>
          {/* Der eigentliche Ausloeser dieses Pakets: eine Teilnehmerin hatte
              per gewoehnlicher Mail geantwortet, und diese Antwort taucht hier
              per Konstruktion NICHT auf. Das auszusprechen ist billiger als ein
              Organisator, der auf eine Zusage wartet, die er laengst hat. */}
          {fehlend.length > 0 && (
            <p className="px-1 text-xs leading-relaxed text-text-3">
              {t.calendar.polls.keineAntwortErklaerung}
            </p>
          )}
        </section>

        {poll.graph_web_link && (
          <a
            href={poll.graph_web_link}
            target="_blank"
            rel="noreferrer"
            data-poll-event-link="1"
            className="inline-flex items-center gap-2 text-sm text-accent-600 hover:underline"
          >
            <Check className="h-4 w-4" aria-hidden />
            {t.calendar.polls.gebuchtenTerminOeffnen}
          </a>
        )}
      </div>
    </div>
  );
}

function Kennzahl({
  label,
  wert,
  betont,
}: {
  label: string;
  wert: number;
  betont?: boolean;
}) {
  return (
    <div
      data-poll-stat={label}
      className="rounded-xl border border-border bg-surface px-3 py-2.5 text-center"
    >
      <div
        className={[
          "font-numeric text-xl font-semibold tabular-nums leading-none",
          betont ? "text-accent-600" : "text-text",
        ].join(" ")}
      >
        {wert}
      </div>
      <div className="mt-1 text-xs text-text-2">{label}</div>
    </div>
  );
}

// ── Die Seite ────────────────────────────────────────────────────────────────

export default function AbstimmungenPage() {
  const t = useT();
  const { pollId } = useParams();
  useEffect(() => {
    document.title = t.calendar.polls.dokumentTitel;
  }, [t]);
  return pollId ? <Detail pollId={pollId} /> : <Liste />;
}
